import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CreditCard,
  Users,
  DollarSign,
  ChevronRight,
  AlertCircle,
  Loader2,
  Hash,
  FileText,
  Clock,
} from 'lucide-react';
import { useUserProfile } from '../../context/UserProfileContext';
import { api } from '../../lib/api';
import {
  type TierItem,
  intervalLabel,
  intervalLabelFull,
  calculateTieredTotal,
} from '../../lib/billing';

interface StripeData {
  customerId?: string;
  subscriptionId?: string;
  status?: string;
  subscriptionQuantity?: number;
  priceId?: string;
  productId?: string;
  productName?: string;
  priceNickname?: string;
  currentPeriodEnd?: number;
  cancelAtPeriodEnd?: boolean;
  lastPaymentFailed?: boolean;
  trialEnd?: number | null;
}

interface PriceDetails {
  unitAmount: number;
  currency: string;
  interval: string;
  billingScheme: string;
  nickname: string | null;
  isFlatRate: boolean;
  tiersMode: string | null;
  tiers: TierItem[] | null;
}

function daysUntil(unixSeconds: number): number {
  const ms = unixSeconds * 1000 - Date.now();
  if (ms <= 0) return 0;
  return Math.ceil(ms / (1000 * 60 * 60 * 24));
}

export function ManageBillingPage() {
  const navigate = useNavigate();
  const { currentUser, adminOrg } = useUserProfile();

  const [billing, setBilling] = useState<StripeData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [price, setPrice] = useState<PriceDetails | null>(null);
  const [priceError, setPriceError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const subPromise = api
      .get<{ success: boolean; data: StripeData | null }>('/billing/subscription')
      .then((res) => { if (!cancelled) setBilling(res.data); })
      .catch((err: Error) => { if (!cancelled) setError(err.message); });

    const pricePromise = api
      .get<{ success: boolean; data: PriceDetails }>('/billing/price')
      .then((res) => { if (!cancelled) setPrice(res.data); })
      .catch((err: unknown) => {
        if (!cancelled) {
          const msg = err instanceof Error ? err.message : String(err);
          setPriceError(msg);
          console.error('[ManageBillingPage] /billing/price failed:', msg);
        }
      });

    Promise.all([subPromise, pricePromise]).finally(() => {
      if (!cancelled) setLoading(false);
    });

    return () => { cancelled = true; };
  }, []);

  if (!currentUser?.isPlatformAdmin && !adminOrg) {
    return (
      <div className="px-4 pt-6 pb-8 md:px-8 md:max-w-2xl md:mx-auto">
        <p className="text-small text-slate text-center py-16">
          You don't have access to billing management.
        </p>
      </div>
    );
  }

  const seats = billing?.subscriptionQuantity ?? 0;
  const hasSubscription = !!billing?.subscriptionId;
  const interval = price?.interval ?? 'month';

  // Detect flat-rate (non-per-user) plan
  const isFlatRate = !!(
    price?.isFlatRate ||
    billing?.priceNickname?.toLowerCase().includes('flat')
  );

  // Calculate pricing for display
  let pricePerSeat: number | null = null;
  let totalCost: number | null = null;

  if (price) {
    if (isFlatRate) {
      totalCost = price.unitAmount / 100;
    } else if (price.billingScheme === 'tiered' && price.tiers) {
      totalCost = calculateTieredTotal(price.tiers, seats, price.tiersMode) / 100;
      pricePerSeat = seats > 0 ? totalCost / seats : null;
    } else if (price.unitAmount !== null) {
      pricePerSeat = price.unitAmount / 100;
      totalCost = seats * pricePerSeat;
    }
  }

  return (
    <div className="px-4 pt-6 pb-8 md:px-8 md:max-w-2xl md:mx-auto">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-1 text-small mb-4 -ml-1">
        <button
          type="button"
          onClick={() => navigate('/admin')}
          className="text-slate hover:text-midnight transition-colors duration-150"
        >
          Admin Panel
        </button>
        <ChevronRight size={14} className="text-stone" />
        <button
          type="button"
          onClick={() => navigate('/admin/company')}
          className="text-slate hover:text-midnight transition-colors duration-150"
        >
          Company
        </button>
        <ChevronRight size={14} className="text-stone" />
        <span className="text-midnight font-medium">Plan &amp; Billing</span>
      </nav>

      <h1 className="font-display text-h1 text-midnight mb-1">Plan &amp; Billing</h1>
      <p className="text-small text-slate mb-6">
        {adminOrg ? adminOrg.orgName : 'Subscription overview'}
      </p>

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-16">
          <Loader2 size={24} className="animate-spin text-plum" />
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 flex items-start gap-3">
          <AlertCircle size={18} className="shrink-0 text-red-600 mt-0.5" />
          <p className="text-small text-red-700">{error}</p>
        </div>
      )}

      {/* Payment failed banner */}
      {billing?.lastPaymentFailed && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 flex items-start gap-3">
          <AlertCircle size={18} className="shrink-0 text-red-600 mt-0.5" />
          <div>
            <p className="text-small font-semibold text-red-800">Payment failed</p>
            <p className="text-[13px] text-red-700 mt-1">
              Your last invoice payment failed. Please update your payment method.
            </p>
          </div>
        </div>
      )}

      {!loading && !hasSubscription && !error && (
        <div className="bg-mist border border-stone rounded-xl p-4 mb-6">
          <p className="text-small text-slate leading-relaxed">
            No active subscription. Use "Manage Plan" below to set up billing.
          </p>
        </div>
      )}

      {/* Trial period banner */}
      {!loading && hasSubscription && billing?.status === 'trialing' && (
        <div className="bg-plum/5 border border-plum/20 rounded-xl p-4 mb-4 flex items-start gap-3">
          <Clock size={18} className="shrink-0 text-plum mt-0.5" />
          <div>
            <p className="text-small font-semibold text-plum">Trial period active</p>
            {billing.trialEnd ? (
              <p className="text-[13px] text-midnight mt-1">
                <span className="font-medium">{daysUntil(billing.trialEnd)} days</span> remaining.
                Billing begins on {new Date(billing.trialEnd * 1000).toLocaleDateString()}.
              </p>
            ) : (
              <p className="text-[13px] text-midnight mt-1">
                Your subscription is in a free trial. Billing will begin at the end of the trial period.
              </p>
            )}
          </div>
        </div>
      )}

      {/* Billing Summary Cards */}
      {!loading && hasSubscription && (
        <div className={`grid grid-cols-1 gap-3 mb-6 ${isFlatRate ? 'sm:grid-cols-2' : 'sm:grid-cols-3'}`}>
          {isFlatRate ? (
            <>
              {/* Plan name */}
              <div className="bg-white border border-stone rounded-xl p-4 shadow-brand-sm">
                <div className="flex items-center gap-2 mb-2">
                  <CreditCard size={16} className="text-plum" />
                  <span className="font-body text-label uppercase tracking-wider text-slate">
                    Plan
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <p className="font-display text-[22px] font-medium text-midnight leading-tight">
                    {billing?.productName || billing?.priceNickname || 'Plan'}
                  </p>
                  {totalCost === 0 && (
                    <span className="text-[11px] font-semibold uppercase text-green-700 bg-green-50 border border-green-200 rounded-full px-1.5 py-0.5">
                      Free
                    </span>
                  )}
                </div>
                <p className="text-[13px] text-slate mt-1 capitalize">
                  {billing?.status === 'trialing' ? 'trial' : billing?.status ?? 'active'}
                </p>
              </div>

              {/* Monthly rate */}
              {totalCost !== null ? (
                <div className="bg-white border border-stone rounded-xl p-4 shadow-brand-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <DollarSign size={16} className="text-plum" />
                    <span className="font-body text-label uppercase tracking-wider text-slate">
                      {totalCost === 0 ? 'Cost' : `${intervalLabelFull(interval)} Rate`}
                    </span>
                  </div>
                  <p className={`font-mono text-[28px] font-medium leading-none ${totalCost === 0 ? 'text-green-700' : 'text-midnight'}`}>
                    {totalCost === 0 ? 'Free' : `$${totalCost.toFixed(2)}`}
                  </p>
                  <p className="text-[13px] text-slate mt-1">
                    {totalCost === 0 ? 'no charge' : `flat rate / ${intervalLabel(interval)} (USD)`}
                  </p>
                </div>
              ) : priceError ? (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 shadow-brand-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertCircle size={16} className="text-amber-600" />
                    <span className="font-body text-label uppercase tracking-wider text-amber-700">
                      Price Error
                    </span>
                  </div>
                  <p className="text-[12px] font-mono text-amber-700 break-all leading-relaxed">
                    {priceError}
                  </p>
                </div>
              ) : (
                <div className="bg-white border border-stone rounded-xl p-4 shadow-brand-sm flex items-center justify-center">
                  <Loader2 size={18} className="animate-spin text-slate" />
                </div>
              )}
            </>
          ) : (
            <>
              {/* Seats */}
              <div className="bg-white border border-stone rounded-xl p-4 shadow-brand-sm">
                <div className="flex items-center gap-2 mb-2">
                  <Users size={16} className="text-plum" />
                  <span className="font-body text-label uppercase tracking-wider text-slate">
                    Users
                  </span>
                </div>
                <p className="font-mono text-[28px] font-medium text-midnight leading-none">
                  {seats}
                </p>
                <p className="text-[13px] text-slate mt-1">active users</p>
              </div>

              {/* Price per user */}
              {pricePerSeat !== null ? (
                <div className="bg-white border border-stone rounded-xl p-4 shadow-brand-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <DollarSign size={16} className="text-plum" />
                    <span className="font-body text-label uppercase tracking-wider text-slate">
                      Per User
                    </span>
                  </div>
                  <p className="font-mono text-[28px] font-medium text-midnight leading-none">
                    ${pricePerSeat.toFixed(2)}
                  </p>
                  <p className="text-[13px] text-slate mt-1">
                    / {intervalLabel(interval)} (USD)
                    {price?.billingScheme === 'tiered' && (
                      <span className="block text-[11px] text-slate/70 mt-0.5">
                        effective rate ({intervalLabelFull(interval).toLowerCase()} tiered)
                      </span>
                    )}
                  </p>
                </div>
              ) : priceError ? (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 shadow-brand-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertCircle size={16} className="text-amber-600" />
                    <span className="font-body text-label uppercase tracking-wider text-amber-700">
                      Price Error
                    </span>
                  </div>
                  <p className="text-[12px] font-mono text-amber-700 break-all leading-relaxed">
                    {priceError}
                  </p>
                </div>
              ) : (
                <div className="bg-white border border-stone rounded-xl p-4 shadow-brand-sm flex items-center justify-center">
                  <Loader2 size={18} className="animate-spin text-slate" />
                </div>
              )}

              {/* Total cost */}
              {totalCost !== null ? (
                <div className="bg-white border border-stone rounded-xl p-4 shadow-brand-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <CreditCard size={16} className="text-plum" />
                    <span className="font-body text-label uppercase tracking-wider text-slate">
                      {intervalLabelFull(interval)} Total
                    </span>
                  </div>
                  <p className="font-mono text-[28px] font-medium text-midnight leading-none">
                    ${totalCost.toFixed(2)}
                  </p>
                  <p className="text-[13px] text-slate mt-1">
                    {price?.billingScheme === 'tiered'
                      ? `${seats} users (tiered)`
                      : `${seats} users × $${pricePerSeat?.toFixed(2)}`}
                  </p>
                </div>
              ) : !priceError ? (
                <div className="bg-white border border-stone rounded-xl p-4 shadow-brand-sm flex items-center justify-center">
                  <Loader2 size={18} className="animate-spin text-slate" />
                </div>
              ) : null}
            </>
          )}
        </div>
      )}

      {!loading && (
        <div className="flex flex-col gap-3">
          {/* Manage Plan */}
          <button
            type="button"
            onClick={() => navigate('/admin/company/billing/plan')}
            className="w-full text-left bg-white border border-stone rounded-xl p-4 shadow-brand-sm
                       flex items-center gap-4
                       hover:border-plum/40 hover:shadow-brand-md active:bg-mist
                       transition-all duration-150"
          >
            <div className="shrink-0 w-10 h-10 rounded-lg bg-plum/10 flex items-center justify-center">
              <Hash size={20} className="text-plum" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-small text-midnight">Manage Plan</p>
              <p className="text-[13px] text-slate mt-0.5 leading-snug">
                {hasSubscription
                  ? isFlatRate
                    ? 'View your plan, switch plans, or apply a coupon.'
                    : 'View your plan, change user count, switch billing period, or apply a coupon.'
                  : 'Set up your subscription and start billing.'}
              </p>
            </div>
            <ChevronRight size={18} className="shrink-0 text-slate" />
          </button>

          {/* Manage Billing */}
          <button
            type="button"
            onClick={() => navigate('/admin/company/billing/payments')}
            className="w-full text-left bg-white border border-stone rounded-xl p-4 shadow-brand-sm
                       flex items-center gap-4
                       hover:border-plum/40 hover:shadow-brand-md active:bg-mist
                       transition-all duration-150"
          >
            <div className="shrink-0 w-10 h-10 rounded-lg bg-plum/10 flex items-center justify-center">
              <FileText size={20} className="text-plum" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-small text-midnight">Manage Billing</p>
              <p className="text-[13px] text-slate mt-0.5 leading-snug">
                {hasSubscription
                  ? 'Update payment method and view invoices.'
                  : 'Payment and invoice management available after subscribing.'}
              </p>
            </div>
            <ChevronRight size={18} className="shrink-0 text-slate" />
          </button>
        </div>
      )}
    </div>
  );
}
